import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, Download, QrCode, Share2, ArrowLeft, 
  Mail, Phone, Globe, Building2, Loader, Copy, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';
import { LOGO_DARK_BG } from '../config/branding';

// Company email for all business cards
const COMPANY_EMAIL = 'info@dein-gutachten-direkt.de';

const VertriebVisitenkartePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cardData, setCardData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const token = localStorage.getItem('salesperson_token');
  const salespersonId = localStorage.getItem('salesperson_id');
  const salespersonName = localStorage.getItem('salesperson_name');

  useEffect(() => {
    if (!token || !salespersonId) {
      navigate('/vertrieb/login');
      return;
    }
    loadBusinessCard();
  }, [token, salespersonId, navigate]);

  const loadBusinessCard = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/${salespersonId}/business-card`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setCardData(data);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadVCard = () => {
    if (!cardData) return;
    
    const blob = new Blob([cardData.vcard_content], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = cardData.vcard_filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadQR = () => {
    if (!cardData) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${cardData.qr_code_base64}`;
    link.download = `qr_visitenkarte_${cardData.name.replace(' ', '_')}.png`;
    link.click();
  };

  const copyLink = () => {
    const cardUrl = `${window.location.origin}/visitenkarte/${salespersonId}`;
    navigator.clipboard.writeText(cardUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareCard = async () => {
    const cardUrl = `${window.location.origin}/visitenkarte/${salespersonId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Visitenkarte - ${cardData?.name}`,
          text: 'Meine digitale Visitenkarte bei Dein Gutachten Direkt',
          url: cardUrl
        });
      } catch (err) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/vertrieb/dashboard')} className="text-white/70 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-white">Meine Visitenkarte</h1>
          </div>
          <span className="text-white/50 text-sm">{getVersionString()}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Card Preview */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-white text-center">
              <img 
                src={LOGO_DARK_BG} 
                alt="Dein Gutachten Direkt" 
                className="h-12 mx-auto mb-4"
              />
              <h2 className="text-2xl font-bold">{cardData?.name}</h2>
              <p className="opacity-90">Vertriebsmitarbeiter</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 text-gray-700">
                <Mail className="w-5 h-5 text-brand-green" />
                <span>{COMPANY_EMAIL}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Phone className="w-5 h-5 text-brand-green" />
                <span>+49 4101 847 11 60</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Globe className="w-5 h-5 text-brand-green" />
                <span>www.dein-gutachten-direkt.de</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Building2 className="w-5 h-5 text-brand-green" />
                <span>Dein Gutachten Direkt GmbH</span>
              </div>
            </div>
          </div>

          {/* QR Code & Actions */}
          <div className="space-y-6">
            {/* QR Code */}
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <h3 className="font-bold text-gray-900 mb-4">QR-Code Visitenkarte</h3>
              {cardData?.qr_code_base64 && (
                <img 
                  src={`data:image/png;base64,${cardData.qr_code_base64}`}
                  alt="QR Code Visitenkarte"
                  className="w-48 h-48 mx-auto rounded-xl shadow-md"
                />
              )}
              <p className="text-sm text-gray-600 mt-4">
                Scannen Sie den Code um den Kontakt zu speichern
              </p>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-6 space-y-3">
              <h3 className="font-bold text-gray-900 mb-4">Downloads & Teilen</h3>
              
              <Button
                onClick={downloadVCard}
                className="w-full bg-brand-green hover:bg-brand-green-dark text-white justify-start"
              >
                <Download className="w-5 h-5 mr-3" />
                Visitenkarte herunterladen (.vcf)
              </Button>
              
              <Button
                onClick={downloadQR}
                variant="outline"
                className="w-full justify-start"
              >
                <QrCode className="w-5 h-5 mr-3" />
                QR-Code herunterladen (.png)
              </Button>
              
              <Button
                onClick={shareCard}
                variant="outline"
                className="w-full justify-start"
              >
                <Share2 className="w-5 h-5 mr-3" />
                Visitenkarte teilen
              </Button>
              
              <Button
                onClick={copyLink}
                variant="outline"
                className="w-full justify-start"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-5 h-5 mr-3 text-green-600" />
                    Link kopiert!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 mr-3" />
                    Link kopieren
                  </>
                )}
              </Button>
            </div>

            {/* Wallet Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-semibold text-blue-800 mb-2">
                📱 Tipp: Zum Wallet hinzufügen
              </h4>
              <p className="text-sm text-blue-700">
                Laden Sie die .vcf Datei herunter und öffnen Sie sie auf Ihrem Smartphone. 
                Der Kontakt kann dann direkt gespeichert oder zu Apple/Google Kontakte hinzugefügt werden.
              </p>
            </div>
          </div>
        </div>

        {/* Full HTML Preview */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="font-bold text-gray-900">Vorschau der Web-Visitenkarte</h3>
            <p className="text-sm text-gray-600">
              Diese Seite können Sie mit Kunden teilen: 
              <a 
                href={`/visitenkarte/${salespersonId}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-brand-green ml-1 hover:underline"
              >
                {window.location.origin}/visitenkarte/{salespersonId}
              </a>
            </p>
          </div>
          {cardData?.html_card && (
            <iframe
              srcDoc={cardData.html_card}
              title="Visitenkarte Vorschau"
              className="w-full h-[600px] border-0"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VertriebVisitenkartePage;
